import React, { useState } from 'react';
import { Edit2, Save, XCircle, Trash2 } from 'lucide-react'; // ✨ 新增 Trash2 圖示

const StaffView = ({
  isOpen,
  onClose,
  staffList = [],
  onUpdateName
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ id: '', name: '' });

  if (!isOpen) return null;

  const startEditing = (staff) => {
    setEditingId(staff.id);
    setEditForm({ id: staff.id, name: staff.name });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ id: '', name: '' });
  };

  const saveEditing = async () => {
    if (!editForm.name.trim()) return alert("名字不能為空");
    if (!editForm.id) return alert("ID 不能為空");

    try {
      const res = await fetch('/api/staff/updateInfo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oldId: editingId,
          newId: editForm.id,
          newName: editForm.name
        })
      });

      const result = await res.json();
      if (result.success) {
        window.location.reload();
      } else {
        alert("❌ 修改失敗：" + result.message);
      }
    } catch (err) {
      console.error(err);
      alert("系統錯誤");
    }
  };

  // 🗑️ 新增：處理刪除邏輯
  const handleDelete = async (staff) => {
    // ⚠️ 雙重確認，避免手滑
    if (!window.confirm(`確定要「永久刪除」 ${staff.name} (ID: ${staff.id}) 嗎？`)) {
      return;
    }

    try {
      const res = await fetch('/api/staff/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: staff.id })
      });

      const result = await res.json();
      if (result.success) {
        // alert("刪除成功");
        window.location.reload(); // 刪除後重整頁面
      } else {
        alert("❌ 刪除失敗：" + result.message);
      }
    } catch (err) {
      console.error(err);
      alert("系統錯誤");
    }
  };

  const filteredList = staffList.filter(staff =>
    staff.name.includes(searchTerm) || String(staff.id).includes(searchTerm)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center overflow-auto p-2">
      <div className="transition-all duration-300 transform bg-white rounded-lg shadow-2xl w-[80%] max-w-2xl mx-auto flex flex-col max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-teal-600 text-white shrink-0">
          <h2 className="text-xl font-bold flex items-center gap-2">
            人員資料管理
          </h2>
          <button onClick={onClose} className="hover:bg-white/20 rounded-full p-1 transition-colors">
            <XCircle size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 bg-gray-50 border-b shrink-0">
          <input
            type="text"
            placeholder="🔍 搜尋姓名 或 ID..."
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500 text-base"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={editingId !== null}
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-2 bg-gray-100">
          <ul className="space-y-2 pb-4">
            {filteredList.map((staff) => (
              <li key={staff.id} className={`p-3 rounded-lg shadow-sm border-l-4 transition-all 
                ${editingId === staff.id ? 'bg-white border-blue-500 ring-2 ring-blue-100' : 'bg-white border-teal-400'}`}>

                {editingId === staff.id ? (
                  // --- 編輯模式 ---
                  <div className="flex flex-col gap-3 w-full">
                    <div className="flex gap-2 items-center w-full">
                      <div className="relative w-20 shrink-0">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">ID</span>
                        <input
                          type="number"
                          value={editForm.id}
                          onChange={(e) => setEditForm({ ...editForm, id: e.target.value })}
                          className="w-full pl-6 pr-1 py-2 border rounded focus:border-blue-500 focus:outline-none font-mono text-center"
                        />
                      </div>

                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="flex-1 p-2 border rounded focus:border-blue-500 focus:outline-none min-w-0"
                        placeholder="姓名"
                      />
                    </div>

                    <div className="flex gap-2 justify-end border-t pt-2 mt-1">
                      <button onClick={cancelEditing} className="flex-1 sm:flex-none px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex justify-center items-center gap-1">
                        <XCircle size={16} /> 取消
                      </button>
                      <button onClick={saveEditing} className="flex-1 sm:flex-none px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex justify-center items-center gap-1 shadow-sm">
                        <Save size={16} /> 儲存
                      </button>
                    </div>
                  </div>
                ) : (
                  // --- 檢視模式 ---
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <span className="bg-gray-100 text-gray-500 text-sm font-mono px-2 py-0.5 rounded shrink-0">
                        #{staff.id}
                      </span>
                      <span className="text-lg font-medium text-gray-800 truncate">
                        {staff.name}
                        {staff.shift && <span className="text-xs text-gray-400 ml-2 font-normal">({staff.shift})</span>}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* 編輯按鈕 */}
                      <button
                        onClick={() => startEditing(staff)}
                        className="p-2 text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-full transition-colors"
                        title="編輯"
                      >
                        <Edit2 size={20} />
                      </button>

                      {/* ✨ 刪除按鈕 (紅色) */}
                      <button
                        onClick={() => handleDelete(staff)}
                        className="p-2 text-red-500 bg-red-50 hover:bg-red-100 rounded-full transition-colors"
                        title="刪除"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StaffView;